import React, { useEffect, useMemo, useState } from 'react';
import { Seo } from '../components/Seo';
import { Link, useSearchParams } from 'react-router-dom';
import { FilePlus2, FolderOpen, CheckCircle2, Clock, AlertCircle, Mail, Phone, ShoppingCart, Trash2, Pencil, X, FileText } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';
import { nominatorApi, useNominatorAuth } from '@/context/NominatorAuthContext';

const BASE_PRICE_PAISE = 750000; // ₹7,500 base
const GST_RATE = 0.18;
const PER_ENTRY_TOTAL_PAISE = Math.round(BASE_PRICE_PAISE * (1 + GST_RATE)); // ₹8,850

const inr = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

// Portal home. Shows the submitter's identity, all nominations (grouped
// into drafts + submitted), the top-line counters, and — Wave 3 — a
// unified Cart to combine multiple drafts into a single Razorpay checkout
// OR an offline payment request.
const PortalDashboard = () => {
  usePageTitle('Nominator Dashboard');
  const { nominator } = useNominatorAuth();
  const [nominations, setNominations] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [showCart, setShowCart] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    try {
      const [{ data: noms }, { data: subs }] = await Promise.all([
        nominatorApi.get('/nominator/nominations'),
        nominatorApi.get('/nominator/submissions'),
      ]);
      setNominations(Array.isArray(noms) ? noms : []);
      setSubmissions(Array.isArray(subs) ? subs : []);
    } catch {
      setNominations([]);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Auto-open cart if user was routed here from the form with ?cart=1
  useEffect(() => {
    if (params.get('cart') === '1') {
      setShowCart(true);
      // Clean the URL so refresh doesn't loop
      const p = new URLSearchParams(params);
      p.delete('cart');
      setParams(p, { replace: true });
    }
     
  }, []);

  // Only fully-completed drafts qualify for checkout (per user rule — Billing
  // page must not show incomplete entries). Incomplete drafts still show in
  // the "Drafts" list so users can finish them, but they are hidden from
  // the cart selection UI + skipped from the cart summary count.
  const drafts = nominations.filter((n) => n.status === 'draft' || (n.status === 'payment_pending' && n.payment_status !== 'paid'));
  const cartEligibleDrafts = drafts.filter((n) => n.is_complete);
  const submittedPaid = nominations.filter((n) => n.payment_status === 'paid');

  const totals = {
    total: nominations.length,
    drafts: drafts.length,
    paid: submittedPaid.length,
    pending: nominations.filter((n) => n.status === 'payment_pending' && n.payment_status !== 'paid').length,
  };

  const selectedList = useMemo(() => cartEligibleDrafts.filter((d) => selected.has(d.id)), [cartEligibleDrafts, selected]);
  const selectedTotalPaise = selectedList.length * PER_ENTRY_TOTAL_PAISE;

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const removeDraft = async (id) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    try {
      await nominatorApi.delete(`/nominator/nominations/${id}`);
      toast.success('Draft removed.');
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete draft.');
    }
  };

  const ensureRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const doCheckout = async (mode, extra = {}) => {
    if (selectedList.length === 0) { toast.error('Select at least one nomination to check out.'); return; }
    setCheckingOut(true);
    try {
      const { data } = await nominatorApi.post('/nominator/cart/checkout', {
        nomination_ids: Array.from(selected),
        payment_mode: mode,
        gst_number: extra.gstNumber || '',
        gst_legal_name: extra.gstLegalName || '',
      });
      if (mode === 'offline') {
        toast.success('Offline payment request recorded. We\'ll email bank/UPI details shortly.');
        setSelected(new Set());
        setShowCart(false);
        load();
        return;
      }
      const submission = data?.submission;
      const payment = data?.payment;
      if (!submission || !payment) throw new Error('Payment setup failed.');
      const ok = await ensureRazorpay();
      if (!ok) throw new Error('Could not load payment gateway.');

      const rzp = new window.Razorpay({
        key: payment.key_id,
        amount: payment.amount_paise,
        currency: payment.currency,
        order_id: payment.order_id,
        name: 'AdAura Awards 2026',
        description: `${selectedList.length} nomination${selectedList.length === 1 ? '' : 's'}`,
        image: '/adaura-logo-trim.png',
        prefill: {
          name: nominator?.name || '',
          email: nominator?.email || '',
          contact: nominator?.phone || '',
        },
        theme: { color: '#9F6A2C' },
        handler: async (resp) => {
          try {
            await nominatorApi.post(`/nominator/cart/${submission.id}/verify-payment`, {
              submission_id: submission.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Payment received — ${selectedList.length} nomination${selectedList.length === 1 ? '' : 's'} confirmed.`);
            setSelected(new Set());
            setShowCart(false);
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Payment verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => toast.info('Payment cancelled. You can retry from the cart.'),
        },
      });
      rzp.on('payment.failed', (resp) => {
        toast.error(resp?.error?.description || 'Payment failed. Please retry.');
      });
      rzp.open();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.missing_fields) {
        toast.error(`${detail.message || 'Fix required fields before checkout.'} Open the draft to complete it.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : err.message || 'Checkout failed.');
      }
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-dashboard">
      <Seo title="Nominator Portal · AdAura Awards" description="Private area — not indexed." path="/" noindex />
      <Toaster position="top-center" />
      <header>
        <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)]">Welcome back</div>
        <h1 className="font-[Libre_Baskerville] text-3xl md:text-4xl mt-2 text-[var(--ink)]" data-testid="portal-greeting">
          {(nominator?.name || 'Entrant').split(' ')[0]}
        </h1>
        <p className="text-[var(--ink-soft)] text-sm mt-2">
          {nominator?.organisation ? `Submitting on behalf of ${nominator.organisation} · ` : ''}Signed in as {nominator?.email}
        </p>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="stat-total"   label="Nominations"  value={totals.total}   to="/portal/entries" />
        <Stat testid="stat-drafts"  label="Drafts"       value={totals.drafts}  note="pay to submit" to="/portal/entries?filter=draft" />
        <Stat testid="stat-paid"    label="Paid"         value={totals.paid}    tone="success"       to="/portal/entries?filter=paid" />
        <Stat testid="stat-pending" label="Payment Due"  value={totals.pending} tone="warn"          to="/portal/entries?filter=pending" />
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <Link to="/portal/new" data-testid="cta-new-nomination"
          className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-5 py-3 text-[11px] tracking-[0.28em] uppercase">
          <FilePlus2 size={14} /> Start New Nomination
        </Link>
        <Link to="/portal/entries" data-testid="cta-my-entries"
          className="inline-flex items-center gap-2 border border-[var(--gold-soft)] text-[var(--ink)] px-5 py-3 text-[11px] tracking-[0.28em] uppercase">
          <FolderOpen size={14} /> View All Entries
        </Link>
        {drafts.length > 0 && (
          <button onClick={() => setShowCart(true)} data-testid="cta-open-cart"
            className="inline-flex items-center gap-2 border-2 border-[var(--walnut)] text-[var(--walnut)] px-5 py-3 text-[11px] tracking-[0.28em] uppercase font-semibold">
            <ShoppingCart size={14} /> Checkout Cart {selected.size > 0 && `(${selected.size})`}
          </button>
        )}
      </div>

      {/* Drafts — one row per category, checkbox to add to cart. */}
      {drafts.length > 0 && (
        <section data-testid="portal-drafts-cart">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)]">Drafts · Pay to Submit</div>
            <div className="text-[11px] text-[var(--ink-soft)]">Tick multiple drafts and check out together for a single payment.</div>
          </div>
          <div className="border border-[var(--gold-soft)] bg-white">
            {drafts.map((d, i) => (
              <DraftRow key={d.id} draft={d} idx={i} checked={selected.has(d.id)}
                onToggle={() => toggle(d.id)} onDelete={() => removeDraft(d.id)} />
            ))}
          </div>
          {selected.size > 0 && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-[var(--walnut)] bg-[var(--bg-alt)] px-5 py-4" data-testid="cart-summary-bar">
              <div className="text-[13px] text-[var(--ink)]">
                <strong>{selected.size}</strong> nomination{selected.size === 1 ? '' : 's'} selected · Total <strong>{inr(selectedTotalPaise)}</strong>
              </div>
              <button onClick={() => setShowCart(true)} data-testid="cart-open-btn"
                className="inline-flex items-center gap-2 bg-[var(--walnut)] text-white px-5 py-2.5 text-[11px] tracking-[0.28em] uppercase">
                <ShoppingCart size={13} /> Proceed to Checkout
              </button>
            </div>
          )}
        </section>
      )}

      <section>
        <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)] mb-3">My Submissions</div>
        {loading ? (
          <div className="text-[var(--ink-soft)] text-sm">Loading…</div>
        ) : submissions.length === 0 && submittedPaid.length === 0 ? (
          <div className="border border-dashed border-[var(--gold-soft)] p-8 text-center text-[var(--ink-soft)] text-sm" data-testid="portal-empty-state">
            No submissions yet. Ready to enter? <Link to="/portal/new" className="text-[var(--walnut)] underline">Start your first nomination →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {submissions.map((s) => <SubmissionRow key={s.id} submission={s} />)}
          </div>
        )}
      </section>

      {/* Need help? */}
      <section className="mt-14 border border-[var(--gold-soft)] bg-[var(--bg-alt)] p-6 md:p-7" data-testid="portal-help-card">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[var(--gold)] mb-2">Need Help?</div>
        <p className="text-[var(--ink)] text-[14px] leading-relaxed">
          If you&apos;re facing any issues while filling out or submitting your nomination, feel free to contact us.
        </p>
        <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[13.5px] text-[var(--ink)]">
          <div className="font-serif">Atul Dwivedi</div>
          <a href="mailto:atuldwivedi@marketingmind.in" className="inline-flex items-center gap-2 text-[var(--gold)] hover:text-[var(--walnut)]">
            <Mail size={13} /> atuldwivedi@marketingmind.in
          </a>
          <a href="tel:+919717025198" className="inline-flex items-center gap-2 text-[var(--gold)] hover:text-[var(--walnut)]">
            <Phone size={13} /> +91 97170 25198
          </a>
        </div>
      </section>

      {showCart && (
        <CartModal
          items={selectedList}
          allDrafts={cartEligibleDrafts}
          allIncomplete={drafts.filter((d) => !d.is_complete)}
          selected={selected}
          onToggle={toggle}
          onClose={() => setShowCart(false)}
          onCheckout={doCheckout}
          loading={checkingOut}
        />
      )}
    </div>
  );
};

const Stat = ({ label, value, note, tone, testid, to }) => {
  const toneCls = tone === 'success' ? 'text-emerald-700 bg-emerald-50 border-emerald-200/60'
                : tone === 'warn' ? 'text-amber-700 bg-amber-50 border-amber-200/60'
                : 'text-[var(--ink)] bg-white border-[var(--gold-soft)]';
  const inner = (
    <>
      <div className="text-[9px] tracking-[0.32em] uppercase opacity-70">{label}</div>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {note && <div className="text-[10px] opacity-60 mt-0.5">{note}</div>}
    </>
  );
  if (to) {
    // Clickable card — hover lift + focus ring for keyboard nav.
    return (
      <Link to={to} data-testid={testid}
            className={`border ${toneCls} p-4 block transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[var(--walnut)]/40`}>
        {inner}
      </Link>
    );
  }
  return <div className={`border ${toneCls} p-4`} data-testid={testid}>{inner}</div>;
};

const DraftRow = ({ draft, idx, checked, onToggle, onDelete }) => {
  const title = draft.entry_title || 'Untitled entry';
  const cat = draft.category || '— Category not chosen —';
  const isPending = draft.status === 'payment_pending';
  const isIncomplete = !draft.is_complete;
  const canSelect = !isIncomplete; // block cart selection on incomplete drafts
  return (
    <div className={`p-4 md:p-5 ${idx > 0 ? 'border-t border-[var(--gold-soft)]/40' : ''} ${checked ? 'bg-[var(--bg-alt)]' : ''}`}
         data-testid={`draft-row-${idx}`}>
      {/* Row 1 — checkbox + title/cat + amount */}
      <div className="flex items-start gap-3">
        <label className={`flex items-start gap-3 flex-1 min-w-0 ${canSelect ? 'cursor-pointer' : 'cursor-not-allowed opacity-90'}`}
               data-testid={`draft-toggle-${idx}`}
               title={canSelect ? '' : 'Complete all required fields before adding to cart'}>
          <input type="checkbox" checked={checked} disabled={!canSelect} onChange={canSelect ? onToggle : undefined} className="sr-only" />
          <span className={`w-5 h-5 mt-0.5 border flex items-center justify-center flex-shrink-0 ${
            !canSelect ? 'bg-[var(--gold-soft)]/20 border-[var(--gold-soft)]/50'
            : checked ? 'bg-[var(--walnut)] border-[var(--walnut)]'
            : 'border-[var(--gold-soft)]'}`}>
            {checked && canSelect && <CheckCircle2 size={12} className="text-white" strokeWidth={3} />}
            {!canSelect && <X size={10} className="text-[var(--ink-muted)]" strokeWidth={2.5} />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-serif text-[var(--ink)] text-[15px] md:text-base leading-tight break-words">{title}</div>
            <div className="text-[var(--ink-muted)] text-[11.5px] md:text-[12px] mt-1 break-words">{cat}</div>
            {isIncomplete && (
              <div className="mt-1.5 text-[10.5px] tracking-[0.16em] uppercase text-[#C2613A] font-semibold flex items-center gap-1.5" data-testid={`draft-incomplete-${idx}`}>
                <AlertCircle size={11} /> Incomplete — required fields missing
              </div>
            )}
          </div>
        </label>
        <div className="text-right whitespace-nowrap flex-shrink-0">
          <div className="text-[12.5px] md:text-[13px] text-[var(--ink)]">{inr(PER_ENTRY_TOTAL_PAISE)}</div>
          <div className="text-[9.5px] md:text-[10px] text-[var(--ink-soft)]">incl. GST</div>
        </div>
      </div>
      {/* Row 2 — pending badge + edit + delete, aligned right on mobile */}
      <div className="flex items-center justify-end gap-3 mt-3 pl-8">
        {isPending && (
          <span className="text-[9px] tracking-[0.24em] uppercase text-[#C2613A] border border-[#C2613A]/40 px-1.5 py-0.5 mr-auto">Payment Due</span>
        )}
        <Link to={`/portal/new?draft_id=${draft.id}`}
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.28em] uppercase text-[var(--walnut)] hover:text-[var(--gold)]"
              data-testid={`draft-edit-${idx}`}>
          <Pencil size={11} /> Edit
        </Link>
        <button onClick={onDelete} className="text-[var(--ink-muted)] hover:text-[#C2613A]" data-testid={`draft-delete-${idx}`} aria-label="Delete draft">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
};

const SubmissionRow = ({ submission }) => {
  const status = submission.status;
  const paid = submission.payment?.status === 'paid';
  const mode = submission.payment_mode || submission.payment?.mode || 'razorpay';
  const isOffline = mode === 'offline';
  const badge = paid
    ? { icon: CheckCircle2, tone: 'text-emerald-700 border-emerald-300 bg-emerald-50', label: 'Submitted · Paid' }
    : status === 'payment_pending' && isOffline
      ? { icon: CheckCircle2, tone: 'text-[#5A2810] border-[#C2613A]/50 bg-[#C2613A]/10', label: 'Submitted · Offline (Payment Pending)' }
      : status === 'payment_pending'
        ? { icon: AlertCircle, tone: 'text-amber-800 border-amber-300 bg-amber-50', label: 'Payment Due' }
        : { icon: Clock, tone: 'text-[var(--ink)] border-[var(--gold-soft)] bg-white', label: status || '—' };
  return (
    <div className="border border-[var(--gold-soft)] bg-white p-5 md:p-6" data-testid={`submission-row-${submission.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-soft)]">Submission</div>
          <div className="font-serif text-lg md:text-xl mt-1 text-[var(--ink)]">
            {(submission.categories || []).length} nomination{(submission.categories || []).length === 1 ? '' : 's'}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] tracking-[0.24em] uppercase border ${badge.tone}`}>
          <badge.icon size={12} /> {badge.label}
        </span>
      </div>
      <ul className="mt-4 space-y-1.5 border-t border-[var(--gold-soft)]/60 pt-3">
        {(submission.categories || []).map((c, i) => (
          <li key={i} className="flex justify-between text-[12.5px] text-[var(--ink)]">
            <span className="truncate mr-3">{c || 'Uncategorised'}</span>
            <span className="text-[var(--ink-soft)] whitespace-nowrap">{inr(submission.payment?.line_items?.[i]?.total_paise)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 pt-3 border-t border-[var(--gold-soft)]/60 flex justify-between items-center">
        <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--ink-soft)]">Total {paid ? 'Paid' : 'Payable'}</span>
        <span className="font-serif text-[15px] text-[var(--walnut)]">{inr(submission.payment?.amount_paise)}</span>
      </div>
      {submission.payment?.payment_id && (
        <div className="mt-2 text-[10.5px] text-[var(--ink-soft)] font-mono truncate" title={submission.payment.payment_id}>
          Payment ID · {submission.payment.payment_id}
        </div>
      )}
      {paid && (
        <div className="mt-4 pt-3 border-t border-[var(--gold-soft)]/40 flex justify-end">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/nominator/submissions/${submission.id}/invoice.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`submission-invoice-${submission.id}`}
            className="inline-flex items-center gap-2 border border-[var(--walnut)] text-[var(--walnut)] hover:bg-[var(--walnut)] hover:text-white text-[10px] tracking-[0.32em] uppercase px-4 py-2 transition-colors"
          >
            <FileText size={12} /> Download Invoice (PDF)
          </a>
        </div>
      )}
    </div>
  );
};

/* ---- Cart modal ---- */
const CartModal = ({ items, allDrafts, allIncomplete = [], selected, onToggle, onClose, onCheckout, loading }) => {
  const [mode, setMode] = useState('razorpay');
  // GST fields only apply when the entrant wants a GST-compliant invoice.
  // Both online and offline mode collect them (offline especially so admin
  // can generate the correct invoice when they mark-paid later).
  const [gstNumber, setGstNumber] = useState('');
  const [gstLegalName, setGstLegalName] = useState('');
  const [gstError, setGstError] = useState('');
  const base = items.length * BASE_PRICE_PAISE;
  const gst = Math.round(base * GST_RATE);
  const total = base + gst;

  // Lock body scroll while the cart is open — prevents the dashboard
  // scrolling behind the overlay and eliminates the "background peek"
  // glitch users reported.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const validateGst = (value) => {
    if (!value.trim()) return '';  // GST is optional
    // Standard Indian GSTIN pattern (15 chars)
    const re = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z][Z][0-9A-Z]$/i;
    return re.test(value.trim().toUpperCase()) ? '' : 'Enter a valid 15-character GSTIN (or leave blank).';
  };

  const submit = () => {
    const gstNum = gstNumber.trim().toUpperCase();
    const err = validateGst(gstNum);
    if (err) { setGstError(err); return; }
    if (gstNum && !gstLegalName.trim()) {
      setGstError('Enter the legal business name registered against this GSTIN.');
      return;
    }
    setGstError('');
    onCheckout(mode, { gstNumber: gstNum, gstLegalName: gstLegalName.trim() });
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#1a0e08]/90 backdrop-blur-md flex items-start justify-center overflow-y-auto" data-testid="cart-modal">
      <div className="bg-white border border-[var(--gold-soft)] max-w-2xl w-full my-8 mx-4 p-6 md:p-8 shadow-[0_30px_80px_-30px_rgba(58,36,24,0.55)] relative">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[var(--gold)]">Checkout</div>
            <h3 className="font-serif text-2xl text-[var(--ink)] mt-1">Your Cart</h3>
            <p className="text-[13px] text-[var(--ink-soft)] mt-1">Tick the nominations you want to pay for in this checkout.</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--walnut)] p-1" data-testid="cart-close" aria-label="Close cart"><X size={20} /></button>
        </div>

        <div className="border border-[var(--gold-soft)] max-h-64 overflow-y-auto">
          {allDrafts.length === 0 ? (
            <div className="p-6 text-center text-[var(--ink-soft)] text-sm">
              {allIncomplete.length > 0
                ? `You have ${allIncomplete.length} draft${allIncomplete.length === 1 ? '' : 's'} — complete all required fields to make ${allIncomplete.length === 1 ? 'it' : 'them'} available for checkout.`
                : 'No drafts available. Create a nomination first.'}
            </div>
          ) : allDrafts.map((d, i) => {
            const checked = selected.has(d.id);
            return (
              <label key={d.id} className={`flex items-center gap-3 p-3 border-b border-[var(--gold-soft)]/40 last:border-b-0 cursor-pointer ${checked ? 'bg-[var(--bg-alt)]' : ''}`} data-testid={`cart-item-${i}`}>
                <input type="checkbox" checked={checked} onChange={() => onToggle(d.id)} className="sr-only" />
                <span className={`w-4 h-4 border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[var(--walnut)] border-[var(--walnut)]' : 'border-[var(--gold-soft)]'}`}>
                  {checked && <CheckCircle2 size={10} className="text-white" strokeWidth={3} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-[var(--ink)] truncate">{d.entry_title || 'Untitled'}</div>
                  <div className="text-[11px] text-[var(--ink-soft)] truncate">{d.category || '— pick a category —'}</div>
                </div>
                <div className="text-right text-[12.5px] text-[var(--ink)]">{inr(PER_ENTRY_TOTAL_PAISE)}</div>
              </label>
            );
          })}
        </div>
        {allIncomplete.length > 0 && (
          <p className="text-[11px] text-[var(--ink-soft)] mt-2 italic" data-testid="cart-incomplete-note">
            {allIncomplete.length} draft{allIncomplete.length === 1 ? '' : 's'} hidden — please complete all required fields before they appear here for checkout.
          </p>
        )}

        <div className="mt-4 space-y-1.5">
          <div className="flex justify-between text-[13px] text-[var(--ink-soft)]">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">{inr(base)}</span>
          </div>
          <div className="flex justify-between text-[13px] text-[var(--ink-soft)]">
            <span>GST (18%)</span>
            <span data-testid="cart-gst">{inr(gst)}</span>
          </div>
          <div className="flex justify-between items-baseline pt-3 mt-1 border-t border-[var(--gold-soft)]">
            <span className="text-[11px] tracking-[0.28em] uppercase text-[var(--ink)]">Total Payable</span>
            <span className="font-serif text-2xl text-[var(--walnut)]" data-testid="cart-total">{inr(total)}</span>
          </div>
        </div>

        {/* Payment mode picker */}
        <div className="mt-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-2">Payment Method</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className={`border p-4 cursor-pointer ${mode === 'razorpay' ? 'border-[var(--walnut)] bg-[var(--bg-alt)]' : 'border-[var(--gold-soft)]'}`} data-testid="cart-mode-razorpay">
              <input type="radio" name="mode" value="razorpay" checked={mode === 'razorpay'} onChange={() => setMode('razorpay')} className="sr-only" />
              <div className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded-full border-2 ${mode === 'razorpay' ? 'border-[var(--walnut)]' : 'border-[var(--gold-soft)]'} flex items-center justify-center`}>
                  {mode === 'razorpay' && <span className="w-2 h-2 rounded-full bg-[var(--walnut)]" />}
                </span>
                <div>
                  <div className="text-[13px] text-[var(--ink)] font-semibold">Pay Online</div>
                  <div className="text-[11px] text-[var(--ink-soft)]">Razorpay · Card / UPI / Netbanking</div>
                </div>
              </div>
            </label>
            <label className={`border p-4 cursor-pointer ${mode === 'offline' ? 'border-[var(--walnut)] bg-[var(--bg-alt)]' : 'border-[var(--gold-soft)]'}`} data-testid="cart-mode-offline">
              <input type="radio" name="mode" value="offline" checked={mode === 'offline'} onChange={() => setMode('offline')} className="sr-only" />
              <div className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded-full border-2 ${mode === 'offline' ? 'border-[var(--walnut)]' : 'border-[var(--gold-soft)]'} flex items-center justify-center`}>
                  {mode === 'offline' && <span className="w-2 h-2 rounded-full bg-[var(--walnut)]" />}
                </span>
                <div>
                  <div className="text-[13px] text-[var(--ink)] font-semibold">Offline Payment</div>
                  <div className="text-[11px] text-[var(--ink-soft)]">Bank transfer / UPI · verified manually</div>
                </div>
              </div>
            </label>
          </div>
          {mode === 'offline' && (
            <div className="mt-3 border border-[#C2613A]/60 bg-[#C2613A]/10 px-4 py-3" data-testid="offline-selected-banner">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#8A3F1B] font-bold flex items-center gap-2">
                <AlertCircle size={13} /> Offline Payment Selected
              </div>
              <p className="text-[12px] text-[#5A2810] mt-1.5 leading-relaxed">
                Your nominations will be recorded as <strong>successfully submitted</strong>. Payment status stays <strong>Pending</strong> until the AdAura team verifies your bank/UPI transfer — we&apos;ll email the payment details right after you confirm.
              </p>
            </div>
          )}
        </div>

        {/* GST invoice info — optional. If entered, the admin uses this to
            generate a GST-compliant invoice at payment verification. */}
        <div className="mt-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--gold)] mb-2">GST Invoice <span className="text-[var(--ink-soft)] normal-case tracking-normal">(Optional)</span></div>
          <p className="text-[11.5px] text-[var(--ink-soft)] mb-3 leading-relaxed">
            Provide your GSTIN if you need a GST-compliant invoice from Digi Giant LLP. Leave blank for a regular receipt.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              value={gstNumber}
              onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
              placeholder="27ABCDE1234F1Z5"
              maxLength={15}
              data-testid="cart-gst-number"
              className="border border-[var(--gold-soft)] px-3 py-2.5 text-[13px] tracking-wider font-mono uppercase focus:border-[var(--brand-brass)] outline-none"
            />
            <input
              type="text"
              value={gstLegalName}
              onChange={(e) => setGstLegalName(e.target.value)}
              placeholder="Legal Business Name"
              maxLength={200}
              data-testid="cart-gst-legal-name"
              className="border border-[var(--gold-soft)] px-3 py-2.5 text-[13px] focus:border-[var(--brand-brass)] outline-none"
            />
          </div>
          {gstError && (
            <p className="text-[#C2613A] text-[11.5px] mt-2 font-semibold" data-testid="cart-gst-error">{gstError}</p>
          )}
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={onClose} disabled={loading} data-testid="cart-cancel"
            className="flex-1 border border-[var(--gold-soft)] text-[var(--ink)] py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[var(--gold-soft)]/10 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={loading || items.length === 0}
            data-testid="cart-checkout-confirm"
            className="flex-2 bg-[var(--walnut)] text-white py-3 px-6 text-[11px] tracking-[0.3em] uppercase disabled:opacity-50">
            {loading ? 'Processing…' : mode === 'offline' ? 'Submit — Payment Pending' : 'Proceed to Pay'}
          </button>
        </div>
        <p className="text-[10.5px] text-[var(--ink-soft)] mt-4 text-center">
          {mode === 'razorpay'
            ? 'Powered by Razorpay · GST-compliant invoice will be emailed after payment'
            : 'You will receive an email with payment instructions from the AdAura team.'}
        </p>
      </div>
    </div>
  );
};

export default PortalDashboard;
